// ============================================================================
// PinShown — THE clock-in code control. Lifted verbatim out of am-views.tsx on
// 2026-10-08 (item 818) so the person card and the account manager's
// night-manager screen show the same thing. Nothing about it changed.
//
// ⛔ A code on its own is useless — the worker also has to know where to put
// it, and until 2026-09-22 that address appeared nowhere in this app. Both are
// here, in the worker's own language, ready to copy.
// ⛔ Nothing sends. The button says "copy" because a button that reads "send"
// and does not send is how somebody believes a worker was set up when they
// were not.
// ============================================================================
import { useState } from 'react';
import { Button } from '../../shared/components';
import { clockUrl, joiningMessage } from './joining-message';
import type { WorkerLang } from './types-am';

export function PinShown({
  name, pin, lang, onDone,
}: { name: string; pin: string; lang: WorkerLang; onDone: () => void }) {
  const [copied, setCopied] = useState<'message' | 'link' | null>(null);
  const message = joiningMessage(lang, name.split(' ')[0] || name, pin);

  function copy(what: 'message' | 'link') {
    void navigator.clipboard.writeText(what === 'message' ? message : clockUrl()).then(
      () => setCopied(what),
      () => setCopied(null),
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-tcs-border bg-tcs-raised p-6 text-center">
      <div className="text-[11px] font-bold uppercase tracking-wider text-tcs-text-muted">
        {name}&rsquo;s code
      </div>
      <div className="mt-3 inline-block rounded-xl border border-tcs-border-input bg-tcs-field px-6 py-4
                      font-mono text-3xl font-bold tracking-[0.22em] text-tcs-text">
        {pin}
      </div>
      <div className="mx-auto mt-4 max-w-prose border-l-[3px] border-st-warn-border bg-tcs-surface px-4 py-3 text-left text-sm text-tcs-text-secondary">
        <b className="text-tcs-text">Send them both the link and the code.</b> This app cannot text or
        email, and this is the only time the code will be shown — if it is lost, issue a new one, it
        cannot be looked up.
        <pre className="mt-3 whitespace-pre-wrap rounded-lg border border-tcs-border bg-tcs-raised px-3 py-2 text-[12.5px] text-tcs-text">{message}</pre>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => copy('message')}>
            {copied === 'message' ? 'Copied ✓' : 'Copy the message'}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => copy('link')}>
            {copied === 'link' ? 'Copied ✓' : 'Copy just the link'}
          </Button>
          <span className="text-[11px] text-tcs-text-muted">
            Written in {lang === 'en' ? 'English' : 'Spanish'} — their own language, off their record.
          </span>
        </div>
      </div>
      <div className="mt-4"><Button size="sm" variant="secondary" onClick={onDone}>Done — I have sent it</Button></div>
    </div>
  );
}
